/**
 * RWBR — the gate every AI-written line has to get through.
 *
 * D-027 put the deterministic booth first and made it a precondition: no AI
 * narration until the floor is built and proven repetition-free. The floor is
 * built. This is the piece that lets something be laid on top of it WITHOUT
 * giving up the one property that makes the whole project trustworthy —
 * **the narration can never say something the engine did not roll.**
 *
 * ── WHY A SYSTEM PROMPT IS NOT ENOUGH ──────────────────────────────────────
 *
 * `worker/index.ts` already tells the model not to invent a position, a pass, a
 * wreck, an injury, a quote, a sponsor or a dollar. That instruction is worth
 * having and it is not a guarantee. A model that has been told not to invent a
 * number still invents numbers; a model handed two drivers' names will
 * cheerfully add a third. An instruction is a request. This is a check.
 *
 * So every candidate line is verified against a VOCABULARY built from the same
 * facts the model was given, and anything outside it is refused. The refusal is
 * cheap: the deterministic line is already sitting there, so a rejected line
 * costs nothing but itself.
 *
 * ── WHAT IS CHECKED, AND WHY EACH ONE ──────────────────────────────────────
 *
 *  1. NUMBERS. Every numeral in the line must appear in the facts. This is the
 *     single highest-value check, because a hallucinated number is both the
 *     easiest error for a model to make and the hardest for a reader to catch —
 *     "he led 34 turns" reads exactly as true as "he led 31 turns".
 *
 *  2. NAMES. Every capitalised word that looks like a surname must belong to
 *     somebody in the facts. A fourth driver appearing in a two-car pass is the
 *     other classic failure and it is invisible unless you go looking.
 *
 *  3. ORDINALS AND POSITIONS. "third" and "3rd" are numbers wearing a disguise
 *     and are checked as numbers, because a wrong finishing position is a wrong
 *     result.
 *
 *  4. SUPERLATIVES. Reused from the deterministic editor rather than
 *     reimplemented: "the biggest wreck of the day" is a claim about every
 *     other wreck, and neither the booth nor a model may make it unsupported.
 *
 *  5. LENGTH AND SHAPE. A model asked for two sentences that returns nine has
 *     stopped doing the job it was given.
 *
 * ── WHAT IS DELIBERATELY *NOT* CHECKED ─────────────────────────────────────
 *
 * Adjectives, mood, register, metaphor. The whole point of putting a model on
 * top of the booth is that it writes better prose than a template can, and a
 * verifier that demanded the line be paraphrase-identical to the engine's would
 * be a verifier that refused everything worth having. The rule is narrow and
 * exact: **facts are constrained, language is free.**
 */

import type { Fact } from './researcher.ts';

/** Numbers that carry no claim and are not worth failing a line over. */
const HARMLESS_NUMBERS = new Set(['0', '1', '2', 'one', 'two']);

/**
 * Words that are capitalised for grammar rather than because they name a
 * person. Without this, every sentence-initial word looks like a surname.
 */
/**
 * Capitalised words that are grammar rather than people.
 *
 * This list got longer the day the name check started reading the FIRST word of
 * a sentence as well as the rest (see the name section below). Everything added
 * is closed-class — pronouns, determiners, conjunctions, prepositions, adverbs
 * and the handful of participles an English sentence starts on — so the list
 * cannot grow without bound, and a word on it is a word no person is called.
 *
 * That last part is not an assumption: `cast.test.ts` asserts that none of
 * these ever collides with a surname the driver generator can produce. A
 * collision would silently exempt a real driver from the whole check.
 */
const SENTENCE_WORDS = new Set([
  'A', 'An', 'And', 'As', 'At', 'But', 'By', 'For', 'From', 'He', 'Here', 'His', 'How', 'I',
  'If', 'In', 'It', 'Its', 'Just', 'Now', 'Of', 'On', 'One', 'Or', 'Out', 'So', 'That', 'The',
  'Their', 'Then', 'There', 'These', 'They', 'This', 'To', 'Two', 'Up', 'We', 'What', 'When',
  'Where', 'While', 'Who', 'With', 'You', 'Yes', 'No', 'Not', 'All', 'After', 'Back', 'Down',
  'Even', 'Every', 'First', 'Last', 'Look', 'More', 'Never', 'Off', 'Over', 'Still', 'Through',
  'Under', 'Well', 'Yet',
  // Added when position 0 came under the check.
  'Nobody', 'Somebody', 'Anybody', 'Nothing', 'Something', 'Anything', 'Everybody', 'Everything',
  'Both', 'Neither', 'Either', 'Each', 'Any', 'Some', 'Most', 'Much', 'Many', 'Few', 'Another',
  'Once', 'Twice', 'Again', 'Already', 'Almost', 'Always', 'Away', 'Behind', 'Before', 'Below',
  'Beside', 'Between', 'Beyond', 'Inside', 'Outside', 'Into', 'Onto', 'Since', 'Until', 'Halfway',
  'Coming', 'Going', 'Running', 'Watch', 'Listen', 'Meanwhile', 'Somewhere', 'Nowhere', 'Everywhere',
  'Perhaps', 'Maybe', 'Suddenly', 'Finally', 'Instead', 'Because', 'Though', 'Although', 'Unless',
  'Whatever', 'Whoever', 'Whether', 'Have', 'Had', 'Has', 'Was', 'Were', 'Will', 'Would', 'Could',
  'Should', 'Might', 'Must', 'Can', 'Did', 'Does', 'Do', 'Is', 'Are', 'Be', 'Been', 'Being',
  'She', 'Her', 'Hers', 'Him', 'Them', 'Our', 'Your', 'My', 'Me', 'Us', 'Which', 'Why', 'Whose',
  'Better', 'Worse', 'Longer', 'Harder', 'Closer', 'Clear', 'Right', 'Left', 'Front', 'Behind',
  'Good', 'Bad', 'New', 'Old', 'Big', 'Small', 'Full', 'Half', 'Plenty', 'Enough', 'Hardly',
  'Barely', 'Nearly', 'Rather', 'Quite', 'Very', 'Too', 'Only', 'Also', 'Together', 'Apart',
]);

/** The words in a racing line that are capitalised and are not people. */
const RACING_WORDS = new Set([
  'Big', 'Bottom', 'Cup', 'Escape', 'Fast', 'Feud', 'Flag', 'Fume', 'Green', 'Group', 'Lap',
  'Laps', 'Leader', 'Middle', 'One', 'Pit', 'Problem', 'Race', 'Rare', 'Repair', 'Speedway',
  'Top', 'Trouble', 'Turn', 'Wreck', 'Yellow', 'Superspeedway', 'Raceway', 'Motor', 'Track',
  'International', 'Sunday', 'Saturday', 'Friday',
]);

/**
 * Every capitalised word the name check waves through, in one list.
 *
 * Exported for exactly one reason: a word on it is a word the gate does not
 * check, so if one were ever also a driver's surname that driver would be
 * invisible to the whole mechanism. `cast.test.ts` asserts the two sets never
 * intersect. A list nobody can audit is a list that will eventually be wrong.
 */
export const EXEMPT_WORDS: readonly string[] = [...SENTENCE_WORDS, ...RACING_WORDS];

const ORDINAL_WORDS: Readonly<Record<string, number>> = Object.freeze({
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7, eighth: 8,
  ninth: 9, tenth: 10, eleventh: 11, twelfth: 12,
});

const NUMBER_WORDS: Readonly<Record<string, number>> = Object.freeze({
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, twenty: 20, thirty: 30,
  forty: 40, fifty: 50,
});

// ---------------------------------------------------------------------------
// The vocabulary a line is allowed to use
// ---------------------------------------------------------------------------

export interface Vocabulary {
  /** Every number the facts support, as strings. */
  numbers: Set<string>;
  /** Every name token the facts support — surnames and given names, separately. */
  names: Set<string>;
  /** Whether a superlative is supported at all for this beat. */
  allowSuperlative: boolean;
  /**
   * Lines already spoken this race.
   *
   * ⚠ THESE USED TO GO TO THE MODEL AND NOWHERE ELSE. The worker asked, in the
   * prompt, that the new line not repeat them — and this file's own header says
   * why that is not enough: **a system prompt is a request; this is a check.**
   * Repetition was the one rule in the whole booth that was enforced by asking
   * nicely, and it is the failure mode NARRATION_MODEL §8 calls the one that
   * matters most.
   *
   * Now the gate compares the candidate against them and refuses an echo.
   */
  recent: readonly string[];
}

/**
 * Build the vocabulary from the SAME facts the model was handed.
 *
 * Built from the facts rather than from the race, deliberately: a model that
 * was only shown three facts must not be allowed to be right by accident about
 * a fourth. If it did not see the number, it may not use the number.
 */
export function vocabularyFor(
  facts: readonly Fact[],
  names: readonly string[],
  options: {
    allowSuperlative?: boolean;
    alsoAllow?: readonly string[];
    /**
     * NUMBERS THE LINE IS ENTITLED TO USE that no fact carries — D-EZ-52.
     *
     * The setting a beat is now given (`booth/setting.ts`) has real numbers in
     * it: the length of the track, how many are in the grandstand, which turn of
     * how many. Without this the number gate reads every one of them as invented
     * and refuses the line, which would make the booth WORSE the moment it was
     * given anything to be atmospheric about.
     *
     * Same principle as `alsoAllow`: a number is sayable because it is true and
     * was handed over, not because the gate got looser.
     */
    alsoAllowNumbers?: readonly (string | number)[];
    recent?: readonly string[];
  } = {},
): Vocabulary {
  const numbers = new Set<string>();
  const vocab = new Set<string>();

  for (const n of options.alsoAllowNumbers ?? []) numbers.add(String(n));

  for (const fact of facts) {
    numbers.add(String(fact.value));
    // A fact's own text carries numbers the value does not — "led 12 of the
    // last 14" — so everything printed in it is fair game.
    for (const match of fact.text.matchAll(/\d+/g)) numbers.add(match[0]);
    for (const word of fact.text.split(/[^A-Za-z'-]+/)) {
      if (word.length > 1 && /^[A-Z]/.test(word)) vocab.add(word);
    }
  }
  for (const name of names) {
    for (const part of name.split(/\s+/)) {
      if (part.length > 1) vocab.add(part);
    }
  }
  // Words that are legitimate but are not facts about the race — in practice
  // the names of the three people in the booth. Without this the verifier
  // refuses "Ned?" as an invented name, which is correct reasoning applied to
  // the wrong list: the cast is not something the model made up, it is
  // something WE told it. Passed in rather than imported so `verify.ts` stays a
  // pure gate with no opinion about who is speaking.
  for (const word of options.alsoAllow ?? []) {
    if (word.length > 1) vocab.add(word);
  }

  return {
    numbers,
    names: vocab,
    allowSuperlative: options.allowSuperlative === true,
    recent: options.recent ?? [],
  };
}

// ---------------------------------------------------------------------------
// The verdict
// ---------------------------------------------------------------------------

export type RefusalReason =
  | 'empty'
  | 'too-long'
  | 'too-many-sentences'
  | 'invented-number'
  | 'invented-name'
  | 'unsupported-superlative'
  | 'contains-markup'
  | 'repeats-itself'
  | 'echoes-a-recent-line';

export interface VerifyVerdict {
  ok: boolean;
  reason: RefusalReason | null;
  /** Exactly what tripped it, for the ledger — never a bare "rejected". */
  detail: string | null;
}

const OK: VerifyVerdict = Object.freeze({ ok: true, reason: null, detail: null });

/**
 * How much of a line has to be shared with an earlier one before it is an echo.
 *
 * 0.5 is deliberately not stricter. Racing prose has a small vocabulary of true
 * things — "the leader", "into turn three", "with ten to go" — and a threshold
 * tight enough to catch every rephrasing would refuse every legitimate second
 * sentence about the same part of the racetrack. Half the trigrams shared is a
 * line that has been written before with the nouns changed.
 */
export const ECHO_THRESHOLD = 0.5;
/** Lines shorter than this are compared whole; trigrams on four words are noise. */
const MIN_TRIGRAM_WORDS = 6;

function words(line: string): string[] {
  return line.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean);
}

function trigrams(line: string): Set<string> {
  const w = words(line);
  const out = new Set<string>();
  for (let i = 0; i + 2 < w.length; i++) out.add(`${w[i]} ${w[i + 1]} ${w[i + 2]}`);
  return out;
}

/**
 * Does this line echo something already said? Returns the reason, or null.
 *
 * Exported for the harness, which counts how often the gate has to fire — a
 * refusal rate near zero means the check is not doing anything and a rate near
 * one means the model has run out of things to say.
 */
export function echoesRecent(line: string, recent: readonly string[]): string | null {
  const w = words(line);
  if (w.length === 0) return null;

  for (const previous of recent) {
    const p = words(previous);
    if (p.length === 0) continue;

    // Short lines: compare them whole, because three-word windows on a
    // five-word sentence measure almost nothing.
    if (w.length < MIN_TRIGRAM_WORDS || p.length < MIN_TRIGRAM_WORDS) {
      if (w.join(' ') === p.join(' ')) return 'word for word, a line already spoken';
      continue;
    }

    const mine = trigrams(line);
    const theirs = trigrams(previous);
    if (mine.size === 0 || theirs.size === 0) continue;
    let shared = 0;
    for (const t of mine) if (theirs.has(t)) shared++;
    const overlap = shared / Math.min(mine.size, theirs.size);
    if (overlap >= ECHO_THRESHOLD) {
      return `${Math.round(overlap * 100)}% of it has been said already — "${previous.slice(0, 60)}"`;
    }

    // The opening is what a listener notices first, so an identical five-word
    // start is an echo whatever happens after it.
    if (w.slice(0, 5).join(' ') === p.slice(0, 5).join(' ')) {
      return `opens exactly like a line already spoken — "${previous.slice(0, 60)}"`;
    }
  }
  return null;
}
const no = (reason: RefusalReason, detail: string): VerifyVerdict => ({ ok: false, reason, detail });

/**
 * A model asked for a couple of sentences that returns nine has stopped doing
 * the job. These are the ceiling on a booth line — and they are the REAL one.
 *
 * ⚠ RAISED 18 Aug 2026 (D-EZ-52), from 320 characters and 3 sentences.
 *
 * Howard: *"id like to lift the tokens even more on commentary and allow them
 * to talk a little more."* The token budget was not what was stopping them.
 * THIS was. A line over the ceiling is not trimmed here — it is REFUSED, and the
 * beat falls back to the engine's flat sentence. So every token added to the
 * budget without moving these two numbers would have bought more refusals and
 * *worse* commentary, which is the opposite of what was asked for.
 *
 * 480 is about four spoken sentences. It is not "no limit", and the limit still
 * matters for the same reason it always did: past roughly this length a model
 * stops calling a race and starts writing an essay about one, and the beat after
 * it has nothing left to say.
 */
export const MAX_CHARS = 700;
export const MAX_SENTENCES = 5;

/**
 * ⚠ 480/4 → 700/5, AND NOT FURTHER — D-EZ-61.
 *
 * Howard, 19 Aug 2026: *"Make it as rich as can be."* The temptation is to take
 * the ceiling off, and it would be the wrong move: the richness he is asking for
 * comes from THREE PEOPLE ARGUING, not from one of them talking for longer. An
 * exchange of three answered turns is nine or twelve sentences of broadcast
 * where a single line used to be four — far more than raising this number to a
 * thousand would ever buy, and it reads as a booth instead of an essay.
 *
 * So the per-line ceiling moves only as far as the briefs did (three or four
 * sentences, four or five for the sign-off, plus room to breathe), and every
 * turn of an exchange is measured against it separately. Past roughly this
 * length a voice stops calling a race and starts writing about one, and the
 * person answering him has nothing left to answer.
 */

const SUPERLATIVES =
  /\b(biggest|worst|best|fastest|slowest|greatest|hardest|first ever|never before|most \w+ (?:ever|of the (?:day|season|year)))\b/i;

/**
 * Verify one candidate line against one vocabulary.
 *
 * Pure and synchronous on purpose — this has to be testable without a network,
 * and the tests are the only reason to believe the gate works at all.
 */
export function verifyLine(line: string, vocab: Vocabulary): VerifyVerdict {
  const trimmed = line.trim();
  if (trimmed.length === 0) return no('empty', 'the model returned nothing');
  if (trimmed.length > MAX_CHARS) {
    return no('too-long', `${trimmed.length} characters, ceiling is ${MAX_CHARS}`);
  }

  // Markup is a smell: it means the model is formatting rather than talking,
  // and it is also the shape an injected instruction tends to arrive in.
  if (/[<>{}]|\]\(|\bhttps?:\/\//i.test(trimmed)) {
    return no('contains-markup', 'the line carries markup, a link or a tag');
  }

  const sentences = trimmed.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 0);
  if (sentences.length > MAX_SENTENCES) {
    return no('too-many-sentences', `${sentences.length} sentences, ceiling is ${MAX_SENTENCES}`);
  }

  // A line that says the same clause twice is a line that ran out of things to
  // say. The deterministic editor bans this across a race; here it is banned
  // within the line, which the editor cannot see.
  const clauses = trimmed.toLowerCase().split(/[,.;!?]\s*/).map((c) => c.trim()).filter((c) => c.length > 12);
  if (new Set(clauses).size !== clauses.length) {
    return no('repeats-itself', 'the same clause appears twice in one line');
  }

  // ---- 1 & 3. numbers, including the ones wearing words ------------------
  for (const match of trimmed.matchAll(/\d+/g)) {
    const n = match[0];
    if (HARMLESS_NUMBERS.has(n)) continue;
    if (!vocab.numbers.has(n) && !vocab.numbers.has(String(Number(n)))) {
      return no('invented-number', `"${n}" is not in the facts`);
    }
  }
  for (const [word, value] of Object.entries({ ...ORDINAL_WORDS, ...NUMBER_WORDS })) {
    if (!new RegExp(`\\b${word}\\b`, 'i').test(trimmed)) continue;
    if (HARMLESS_NUMBERS.has(word)) continue;
    if (!vocab.numbers.has(String(value))) {
      return no('invented-number', `"${word}" (${value}) is not in the facts`);
    }
  }

  // ---- 2. names -----------------------------------------------------------
  //
  // ⚠ THIS USED TO SKIP THE FIRST WORD OF EVERY SENTENCE, on the reasoning that
  // "a sentence-initial capital is grammar, not a person." The reasoning is
  // sound and the rule built on it was a hole: `Barkley closes on the leader.`
  // is a sentence-initial capital AND a person, and a fabricated driver could
  // walk straight through the gate simply by being named first. Found by a test
  // that expected a refusal and got an approval.
  //
  // So position 0 is checked too, against the same exempt lists. That trades a
  // hole for a false-refusal risk — a legitimate opener nobody thought to put
  // in `SENTENCE_WORDS` gets the line thrown away — and the trade is correct,
  // because the two costs are wildly asymmetric. A false refusal costs POLISH:
  // the deterministic line, which was already good enough to ship, stays on
  // screen and nobody can tell. A false accept costs a driver who does not
  // exist saying something that never happened, in a game whose entire claim is
  // that the prose is downstream of the dice.
  for (const sentence of sentences) {
    const words = sentence.split(/[^A-Za-z'-]+/).filter(Boolean);
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      if (!/^[A-Z][a-z']+$/.test(word)) continue;
      if (SENTENCE_WORDS.has(word) || RACING_WORDS.has(word)) continue;
      // A number spelled out and capitalised because it opens a sentence —
      // "Fourteen turns in front" — is a NUMBER, and the number check above has
      // already validated it against the facts. Checking it again as a name
      // would refuse a line the gate has already approved.
      const lower = word.toLowerCase();
      if (lower in ORDINAL_WORDS || lower in NUMBER_WORDS) continue;
      if (!vocab.names.has(word)) {
        return no('invented-name', `"${word}" is not a driver in the facts`);
      }
    }
  }

  // ---- 5. has it already said this? ---------------------------------------
  //
  // Not string equality — a model almost never repeats itself word for word.
  // It repeats the SHAPE: same opening, same clause, one name swapped. So the
  // test is trigram overlap, which catches "Barkley closes on the leader" next
  // to "Coffey closes on the leader" while leaving two genuinely different
  // sentences about the same pass alone.
  const echo = echoesRecent(trimmed, vocab.recent);
  if (echo) return no('echoes-a-recent-line', echo);

  // ---- 4. superlatives ----------------------------------------------------
  const sup = trimmed.match(SUPERLATIVES);
  if (sup && !vocab.allowSuperlative) {
    return no('unsupported-superlative', `"${sup[0]}" is a claim about every other moment`);
  }

  return OK;
}
